import vs from 'virtual-scroll';
import prefix from 'prefix';
import create from 'dom-create-element';

window.create = create;

class Smooth {
    constructor(options = {}) {
        this.options = options;

        this._name = this.constructor.name || 'fallback';
        this._extends = this._name != 'Smooth';
        this.transformPrefix = prefix('transform');
        this._listeners = [];

        this.vars = {
            disabled: options.disabled || false,
            direction: options.direction || 'vertical',
            native: options.native || false,
            ease: options.ease || 0.075,
            current: 0,
            last: 0,
            target: 0,
            width: window.innerWidth,
            height: window.innerHeight,
            bounding: 0,
            timer: null,
            interval: null,
            ticking: false
        };

        if (!this.vars.disabled) {
            if (!this.vars.native) {
                this.vs = new vs({
                    limitInertia: options.vs && options.vs.limitInertia || false,
                    mouseMultiplier: options.vs && options.vs.mouseMultiplier || 1,
                    touchMultiplier: options.vs && options.vs.touchMultiplier || 1.5,
                    firefoxMultiplier: options.vs && options.vs.firefoxMultiplier || 30,
                    preventTouch: options.vs && options.vs.preventTouch || true
                });
                this.vs.sensibility = options.vs && options.vs.sensibility || 1;
            } else {
                this.vs = null;    
            }

            this.dom = {
                listener: options.listener || document.body,
                section: options.section || document.querySelector('.scrollable') || null,
                scrollbar: this.vars.native || options.noscrollbar ? null : {
                    state: {
                        clicked: false
                    },
                    el: create({ selector: 'div', styles: `vs-scrollbar vs-${this.vars.direction} vs-scrollbar-${this._name.toLowerCase()}` }),
                    drag: {
                        el: create({ selector: 'div', styles: 'vs-scrolldrag' }),
                        delta: 0,
                        height: 50
                    }
                }
            };

            this.vars.native ? this.dom.section.style.position = 'fixed' : document.body.style.overflow = 'hidden';
        }

        bindAll(this, [
            '_mouseDown',
            '_mouseUp',
            '_mouseMove',
            '_calcScroll',
            '_nativeScrollHandler',
            '_virtualScrollHandler',
            'scrollTo',
            '_update',
            'resize'
        ]);
    }

    init() {
        if (this.vars.disabled)
            return;
        
        this.dom.section.style.position = 'absolute';
        this._addClasses();
        this.vars.native ? this._addFakeScrollHeight() : !this.options.noscrollbar && this._addFakeScrollBar();

        this.bind();
        this.resize(this.vars.width, this.vars.height);
    }

    _addClasses() {
        const type = this.vars.native ? 'native' : 'virtual';
        const direction = this.vars.direction === 'vertical' ? 'y' : 'x';

        this.dom.listener.classList.add(`is-${type}-scroll`);
        this.dom.listener.classList.add(`${direction}-scroll`);
    }

    bind() {
        this.vars.native ? window.addEventListener('scroll', this._nativeScrollHandler) : (this.vs && this.vs.on(this._virtualScrollHandler));
    }

    unbind() {
        this.vars.native ? window.removeEventListener('scroll', this._nativeScrollHandler) : (this.vs && this.vs.off(this._virtualScrollHandler));
    }

    on(listener) {
        if (!this.vars.disabled)
            this._listeners.push(listener);
    }

    off(listener) {
        if (!this.vars.disabled) {
            const index = this._listeners.indexOf(listener);
            if (index !== -1)
                this._listeners.splice(index, 1);
        }
    }

    _addFakeScrollHeight() {
        this.dom.scroll = create({
            selector: 'div',
            styles: 'vs-scroll-view'
        });

        document.body.appendChild(this.dom.scroll);
        document.body.style.height = 'auto';
    }

    _removeFakeScrollHeight() {
        this.dom.listener.removeChild(this.dom.scroll);
    }

    _addFakeScrollBar() {
        this.dom.listener.appendChild(this.dom.scrollbar.el);
        this.dom.scrollbar.el.appendChild(this.dom.scrollbar.drag.el);

        this.dom.scrollbar.el.addEventListener('click', this._calcScroll);
        this.dom.scrollbar.el.addEventListener('mousedown', this._mouseDown);

        document.addEventListener('mousemove', this._mouseMove);
        document.addEventListener('mouseup', this._mouseUp);
    }

    _removeFakeScrollBar() {
        this.dom.scrollbar.el.removeEventListener('click', this._calcScroll);
        this.dom.scrollbar.el.removeEventListener('mousedown', this._mouseDown);

        document.removeEventListener('mousemove', this._mouseMove);
        document.removeEventListener('mouseup', this._mouseUp);

        this.dom.listener.removeChild(this.dom.scrollbar.el);
    }

    _mouseDown(event) {
        event.preventDefault();
        event.which == 1 && (this.dom.scrollbar.state.clicked = true);
        this.dom.listener.classList.add('is-dragging');
    }

    _mouseUp(event) {
        event.preventDefault();
        this.dom.scrollbar.state.clicked = false;
        this.dom.listener.classList.remove('is-dragging');
    }

    _mouseMove(event) {
        this.dom.scrollbar.state.clicked && this._calcScroll(event);
    }

    _calcScroll(event) {
        event.preventDefault();

        const client = this.vars.direction === 'vertical' ? event.clientY : event.clientX;
        const bounds = this.vars.direction === 'vertical' ? this.vars.height : this.vars.width;
        const delta = client * (this.vars.bounding / bounds);

        this.vars.target = delta;
        this._clampTarget();
        this.dom.scrollbar && (this.dom.scrollbar.drag.delta = this.vars.target);

        this._waitScroll();
    }

    _nativeScrollHandler() {
        if (this.vars.interval)
            return;

        this.vars.target = this.vars.direction === 'vertical' ? window.scrollY || window.pageYOffset : window.scrollX || window.pageXOffset;

        this._scrollHandler();
    }

    _virtualScrollHandler(event) {
        if (this.vars.interval)
            return;

        const delta = this.vars.direction === 'vertical' ? event.deltaY : Math.max(event.deltaX + event.deltaY);
        this.vars.target -= delta * this.vs.sensibility;

        this._clampTarget();

        this._scrollHandler();
    }

    _scrollHandler() {
        clearTimeout(this.vars.timer);

        if (!this.vars.ticking) {
            this.vars.ticking = true;
            this.dom.listener.classList.add('is-scrolling');
        }

        this.vars.timer = setTimeout(() => {
            this.vars.ticking = false;
            this.dom.listener.classList.remove('is-scrolling');
        }, 200);
    }

    _waitScroll() {
        if (this.vars.interval)
            return;

        this.vars.last = -1;

        this.vars.interval = setInterval(() => {
            if (Math.abs(this.vars.last - this.vars.current) > 0.5) {
                this._scrollHandler();
            } else {
                clearInterval(this.vars.interval);
                this.vars.interval = null;
            }
            this.vars.last = this.vars.current;
        }, 100);
    }

    _clampTarget() {
        this.vars.target = Math.round(Math.max(0, Math.min(this.vars.target, this.vars.bounding)));
    }

    getTransform(value) {
        return this.vars.direction === 'vertical' ? 'translate3d(0px, ' + value + 'px, 0)' : 'translate3d(' + value + 'px, 0px, 0)';
    }

    getPosition() {
        return (!this.vars.disabled) ? this.vars.current : getWindowPosition(this.vars.direction);
    }

    scrollTo(destination) {
        let offset;
        if (typeof destination === 'number')
            offset = parseInt(destination);
        else
            offset = destination.getBoundingClientRect().top + this.getPosition();

        if (!this.vars.disabled) {
            if (this.vars.native) {
                this.vars.direction === 'vertical' ? window.scrollTo(0, offset) : window.scrollTo(offset, 0);
                this._nativeScrollHandler();
            } else {
                this.vars.target = offset;
                this._clampTarget();
            }

            this._waitScroll();
        } else {
            this.vars.direction === 'vertical' ? TweenMax.to(window, 1, {scrollTo: {x: 0, y: offset}, ease: Cubic.easeOut}) : TweenMax.to(window, 1, {scrollTo: {x: offset, y: 0}, ease: Cubic.easeOut});
        }
    }

    _update(delta, time) {
        if (document.body.classList.contains('no-scroll') || !this.vars.ticking)
            return;

        this.vars.current += (this.vars.target - this.vars.current) * this.vars.ease;
        this.vars.current < 0.1 && (this.vars.current = 0);

        // if (!this._extends) {
        this.dom.section.style[this.transformPrefix] = this.getTransform(-this.vars.current.toFixed(2));
        // }

        if (!this.vars.native && !this.options.noscrollbar) {
            const size = this.dom.scrollbar.drag.height;
            const bounds = this.vars.direction === 'vertical' ? this.vars.height : this.vars.width;
            const value = (Math.abs(this.vars.current) / (this.vars.bounding / (bounds - size))) + (size / .5) - size;
            const clamp = Math.max(0, Math.min(value - size, value + size));

            this.dom.scrollbar.drag.el.style[this.transformPrefix] = this.getTransform(clamp.toFixed(2));
        }

        for (let listener of this._listeners) {
            listener.call(listener, this.vars.current);
        }
    }

    resize(width, height) {
        if (this.vars.disabled)
            return;

        const propriety = this.vars.direction === 'vertical' ? 'height' : 'width';

        this.vars.width = width;
        this.vars.height = height;

        // if (!this._extends) {
        const bounding = this.dom.section.getBoundingClientRect();
        this.vars.bounding = this.vars.direction === 'vertical' ? bounding.height - (this.vars.native ? 0 : this.vars.height) : bounding.right - (this.vars.native ? 0 : this.vars.width);
        // }

        if (!this.vars.native && !this.options.noscrollbar) {
            this.dom.scrollbar.drag.height = this.vars.height * (this.vars.height / (this.vars.bounding + this.vars.height));
            this.dom.scrollbar.drag.el.style[propriety] = `${this.dom.scrollbar.drag.height}px`;
        } else if (this.vars.native && this.dom.scroll) {
            this.dom.scroll.style[propriety] = `${this.vars.bounding}px`;
        } else {
            this._clampTarget();
        }

        this.scrollTo(this.vars.target);
    }

    destroy() {
        if (this.vars.disabled)
            return;

        if (this.vars.native) {
            this.dom.listener.classList.remove('is-native-scroll');
            this._removeFakeScrollHeight();
        } else {
            this.dom.listener.classList.remove('is-virtual-scroll');
            !this.options.noscrollbar && this._removeFakeScrollBar();
        }

        this.vars.direction === 'vertical' ? this.dom.listener.classList.remove('y-scroll') : this.dom.listener.classList.remove('x-scroll');

        this.vs && (this.vs.destroy(), this.vs = null);

        this.unbind();
    }
}

window.Smooth = Smooth;



// WEBPACK FOOTER //
// ./src/js/lib/dom/smooth-scroll.js